// Hours spent on the project's issues. No currency - see the note below.
//
// Owner, 2026-09-16: every resolution carries hours spent, costed at the
// partner's (or the person's own) rate, and the project's hours are for
// the project owner, company admins and super admins only - the card is
// rendered only when /projects/:id/access says canSeeHours, and the API
// refuses everyone else anyway (back/routes/projectHours.js).
//
// NO MONEY ON THIS CARD (owner, 2026-10-08: "on issues just show the hours
// don't show the cost in currency leave that for specialized export"). The
// route no longer sends cost or the rate behind it at all, so there is nothing
// to draw and nothing to hide; the card names where the money lives instead of
// leaving a column of dashes, which reads as "this work cost nothing".
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Table, Tag, Button, DatePicker, Space, Empty, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { url, tokenRef } from "api";

const hoursText = (n) => (n == null ? "not entered" : `${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })} h`);
const STATUS_COLOR = { recorded: "green", approved: "green", pending: "gold", rejected: "red", superseded: "default" };
const SOURCE = { proposal: "Resolution", status: "Status change", submit: "Submit", bulk: "Bulk resolve", deliverable: "Delivery", resolve: "Resolve", guest: "Shared link" };

export default function ProjectHoursCard({ projectId }) {
  const [range, setRange] = useState([null, null]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (range[0]) qs.append("since", range[0].clone().startOf("day").toISOString());
      if (range[1]) qs.append("until", range[1].clone().endOf("day").toISOString());
      const resp = await fetch(`${url}/projects/${projectId}/hours${qs.toString() ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.err || `HTTP ${resp.status}`);
      setData(body);
    } catch (e) {
      message.error(e.message || "Could not load hours");
    } finally {
      setLoading(false);
    }
  }, [projectId, range]);

  useEffect(() => { load(); }, [load]);

  const entriesByIssue = useMemo(() => {
    const m = new Map();
    for (const e of (data && data.entries) || []) {
      if (!m.has(e.issueId)) m.set(e.issueId, []);
      m.get(e.issueId).push(e);
    }
    return m;
  }, [data]);

  const columns = [
    { title: "Issue", dataIndex: "label", width: 110, render: (v, r) => <a href={`/issue-viewer/${r.issueId}`} target="_blank" rel="noopener noreferrer">{v || "open"}</a> },
    { title: "Title", dataIndex: "title", ellipsis: true },
    { title: "Submitted by", key: "who", render: (_, r) => [...new Set([...(r.companies || []), ...(r.people || [])])].join(", ") },
    { title: "Hours", dataIndex: "hours", width: 110, align: "right", render: (v, r) => <>{hoursText(v)}{r.notEntered ? <div style={{ fontSize: 11, color: "#B84B0A" }}>{r.notEntered} without hours</div> : null}</> },
  ];
  const entryColumns = [
    { title: "When", dataIndex: "createdAt", width: 150, render: (v) => moment(v).format("MMM D, YYYY HH:mm") },
    { title: "Who", key: "who", render: (_, e) => [e.name, e.companyName].filter(Boolean).join(" · ") },
    { title: "Via", dataIndex: "source", width: 120, render: (v, e) => `${SOURCE[v] || v}${e.channel && e.channel !== "web" ? ` (${e.channel})` : ""}` },
    { title: "Hours", dataIndex: "hours", width: 100, align: "right", render: hoursText },
    { title: "Status", dataIndex: "status", width: 110, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
  ];

  return (
    <Card
      size="small"
      style={{ marginTop: 16 }}
      title={
        <span>
          Hours spent on issues
          {data ? <span style={{ fontWeight: 400, color: "#666", marginLeft: 10 }}>{hoursText(data.totalHours)}</span> : null}
        </span>
      }
      extra={
        <Space size={8}>
          <DatePicker.RangePicker size="small" value={range} onChange={(v) => setRange(v || [null, null])} allowEmpty={[true, true]} />
          <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
        </Space>
      }
    >
      <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
        Visible to the project owner and company admins only. Rejected and replaced submissions are listed but not counted.
        {data && data.costHidden ? <div style={{ marginTop: 4 }}>{data.costHidden}</div> : null}
      </div>
      {data && !data.rows.length && !(data.entries || []).length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hours submitted yet" />
      ) : (
        <Table
          size="small"
          rowKey="issueId"
          loading={loading}
          columns={columns}
          dataSource={(data && data.rows) || []}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          expandable={{
            expandedRowRender: (r) => (
              <Table size="small" rowKey="id" columns={entryColumns} dataSource={entriesByIssue.get(r.issueId) || []} pagination={false} />
            ),
          }}
          summary={() => (data && data.rows.length ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}><b>Total</b></Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right"><b>{hoursText(data.totalHours)}</b></Table.Summary.Cell>
            </Table.Summary.Row>
          ) : null)}
        />
      )}
    </Card>
  );
}
